import type { QuestionType } from '@lms/shared/types';

export interface ParsedQuestion {
  id?: string;
  question: string;
  type: QuestionType;
  order: number;
  points: number;
  explanation?: string;
  options: {
    text: string;
    isCorrect: boolean;
    order: number;
  }[];
  warnings?: string[];
}

/**
 * Validates a parsed question and returns any warning messages.
 */
export function validateParsedQuestion(q: ParsedQuestion): string[] {
  const warnings: string[] = [];
  if (!q.question || q.question.trim().length === 0) {
    warnings.push('Teks pertanyaan tidak boleh kosong');
  }

  if (q.type === 'MULTIPLE_CHOICE' || q.type === 'TRUE_FALSE') {
    if (!q.options || q.options.length < 2) {
      warnings.push('Minimal harus ada 2 pilihan jawaban');
    }
    const hasCorrect = q.options?.some(o => o.isCorrect);
    if (!hasCorrect) {
      warnings.push('Belum ada kunci jawaban benar yang dipilih');
    }
    const emptyOptions = q.options?.filter(o => !o.text.trim());
    if (emptyOptions && emptyOptions.length > 0) {
      warnings.push(`${emptyOptions.length} pilihan jawaban masih kosong`);
    }
  }

  return warnings;
}

/**
 * Smart Question Parser
 * Supports:
 * 1. JSON array format: `[ { "question": "...", "type": "MULTIPLE_CHOICE", "options": [...] } ]`
 * 2. CSV / TSV tabular format (e.g. Question,Option A,Option B,Option C,Option D,Key,Points)
 * 3. Text / Markdown format with questions, options (A-E), answer keys, and explanations.
 */
export function parseQuestionsFromText(rawText: string, defaultPoints = 10): ParsedQuestion[] {
  const trimmed = rawText.trim();
  if (!trimmed) return [];

  // 1. Try parsing JSON array
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsedJson = JSON.parse(trimmed);
      if (Array.isArray(parsedJson)) {
        const jsonQuestions: ParsedQuestion[] = [];
        parsedJson.forEach((item, index) => {
          if (item && typeof item === 'object' && item.question) {
            const rawOptions = Array.isArray(item.options) ? item.options : [];
            const mappedOptions = rawOptions.map((opt: unknown, optIdx: number) => {
              if (typeof opt === 'string') {
                return {
                  text: opt.trim(),
                  isCorrect: optIdx === 0,
                  order: optIdx + 1,
                };
              }
              const optObj = opt as { text?: string; isCorrect?: boolean };
              return {
                text: String(optObj.text ?? '').trim(),
                isCorrect: Boolean(optObj.isCorrect),
                order: optIdx + 1,
              };
            });

            const q: ParsedQuestion = {
              id: `q-json-${index + 1}-${Date.now()}`,
              question: String(item.question).trim(),
              type: (item.type as QuestionType) || 'MULTIPLE_CHOICE',
              order: index + 1,
              points: Number(item.points) > 0 ? Number(item.points) : defaultPoints,
              explanation: item.explanation ? String(item.explanation).trim() : undefined,
              options: mappedOptions,
            };
            q.warnings = validateParsedQuestion(q);
            jsonQuestions.push(q);
          }
        });
        if (jsonQuestions.length > 0) {
          return jsonQuestions;
        }
      }
    } catch {
      // Fallback to text parsing if JSON parse failed
    }
  }

  // 2. Try parsing CSV/TSV format if commas or tabs and linebreaks dominate
  const lines = rawText
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);
  const firstLine = lines[0] || '';
  const isCsvHeader =
    /^(pertanyaan|question|soal|no|#)/i.test(firstLine) &&
    (firstLine.includes(',') || firstLine.includes('\t') || firstLine.includes(';'));

  if (isCsvHeader && lines.length > 1) {
    const delimiter = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';
    const csvQuestions: ParsedQuestion[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;
      // Basic CSV tokenization
      const parts = line.split(delimiter).map(p => p.trim().replace(/^["']|["']$/g, ''));
      if (parts.length >= 3) {
        const questionText = parts[0];
        const optionsList: { text: string; isCorrect: boolean; order: number }[] = [];
        let keyChar = '';
        let pointsVal = defaultPoints;

        // Check columns: e.g. [Question, OptA, OptB, OptC, OptD, Key, Points]
        // If last column is numeric points
        const lastCol = parts[parts.length - 1];
        let optionEndIdx = parts.length - 1;
        if (/^\d+$/.test(lastCol) && parts.length >= 5) {
          pointsVal = parseInt(lastCol, 10);
          keyChar = parts[parts.length - 2].toUpperCase();
          optionEndIdx = parts.length - 2;
        } else if (/^[a-eA-E]$/i.test(lastCol)) {
          keyChar = lastCol.toUpperCase();
          optionEndIdx = parts.length - 1;
        }

        const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];
        for (let j = 1; j < optionEndIdx; j++) {
          if (parts[j]) {
            const letter = optionLetters[optionsList.length] || '';
            const isCorrect = keyChar ? letter === keyChar : optionsList.length === 0;
            optionsList.push({
              text: parts[j],
              isCorrect,
              order: optionsList.length + 1,
            });
          }
        }

        if (questionText && optionsList.length >= 2) {
          const q: ParsedQuestion = {
            id: `q-csv-${i}-${Date.now()}`,
            question: questionText,
            type: 'MULTIPLE_CHOICE',
            order: csvQuestions.length + 1,
            points: pointsVal,
            options: optionsList,
          };
          q.warnings = validateParsedQuestion(q);
          csvQuestions.push(q);
        }
      }
    }
    if (csvQuestions.length > 0) {
      return csvQuestions;
    }
  }

  // 3. Smart Text / Markdown Parsing
  const questions: ParsedQuestion[] = [];
  let currentQuestion: Partial<ParsedQuestion> | null = null;
  let currentOptions: { id: string; text: string }[] = [];
  let answerKeyId: string | null = null;
  let currentExplanation: string | undefined = undefined;
  let questionCounter = 0;

  // Regex patterns
  // Matches: "1. ", "### 1. ", "1) ", "**1.** ", "١. ", "Soal 1: "
  const questionPattern =
    /^(?:###\s*)?(?:\*\*)?(?:Soal\s*)?(?:\d+|[0-9]+|[٠-٩]+)[.)\-:]\s*(?:\*\*)?\s*(.+)$/i;
  // Matches: "A. ", "A) ", "a. ", "a) ", "A - ", "[A] "
  const optionPattern = /^(?:\[)?([a-eA-E]|[أ-ي])(?:\]|[.)\-:])\s+(.+)$/;
  // Matches: "Kunci: A", "Jawaban: A", "Kunci Jawaban: B", "Answer: C", "Correct: A"
  const answerPattern =
    /^(?:Kunci(?: Jawaban)?|Jawaban|Answer|Correct(?: Answer)?|Key)\s*[:=]\s*([a-eA-E]|[أ-ي]|Benar|Salah|True|False)\.?\s*$/i;
  // Matches: "Pembahasan: ...", "Penjelasan: ...", "Explanation: ..."
  const explanationPattern = /^(?:Pembahasan|Penjelasan|Explanation)\s*[:=]\s*(.+)$/i;

  const pushCurrentQuestion = () => {
    if (currentQuestion && currentQuestion.question) {
      const mappedOptions = currentOptions.map((opt, index) => {
        let isCorrect = false;
        if (answerKeyId) {
          const cleanKey = answerKeyId.trim().toLowerCase();
          if (cleanKey === opt.id.toLowerCase()) {
            isCorrect = true;
          } else if (cleanKey === 'benar' || cleanKey === 'true') {
            isCorrect =
              opt.id.toLowerCase() === 'a' ||
              opt.text.toLowerCase() === 'benar' ||
              opt.text.toLowerCase() === 'true';
          } else if (cleanKey === 'salah' || cleanKey === 'false') {
            isCorrect =
              opt.id.toLowerCase() === 'b' ||
              opt.text.toLowerCase() === 'salah' ||
              opt.text.toLowerCase() === 'false';
          }
        }
        return {
          text: opt.text.trim(),
          isCorrect,
          order: index + 1,
        };
      });

      if (mappedOptions.length > 0) {
        questionCounter++;
        const q: ParsedQuestion = {
          id: `q-txt-${questionCounter}-${Date.now()}`,
          question: currentQuestion.question.trim(),
          type:
            mappedOptions.length === 2 && /^(benar|salah|true|false)$/i.test(mappedOptions[0].text)
              ? ('TRUE_FALSE' as QuestionType)
              : ('MULTIPLE_CHOICE' as QuestionType),
          order: questionCounter,
          points: currentQuestion.points || defaultPoints,
          explanation: currentExplanation,
          options: mappedOptions,
        };
        q.warnings = validateParsedQuestion(q);
        questions.push(q);
      }
    }
  };

  const rawLines = rawText.split(/\r?\n/);

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i].trim();
    if (!line) continue;

    // 1. Check if line is a Question
    const questionMatch = line.match(questionPattern);
    if (questionMatch) {
      pushCurrentQuestion();
      currentQuestion = {
        question: questionMatch[1],
        points: defaultPoints,
      };
      currentOptions = [];
      answerKeyId = null;
      currentExplanation = undefined;
      continue;
    }

    // 2. Check if line is an Option
    const optionMatch = line.match(optionPattern);
    if (optionMatch && currentQuestion) {
      currentOptions.push({
        id: optionMatch[1].toUpperCase(),
        text: optionMatch[2],
      });
      continue;
    }

    // 3. Check if line is an Answer Key
    const cleanLine = line.replace(/\*\*/g, '').replace(/__/g, '').trim();
    const answerMatch = cleanLine.match(answerPattern);
    if (answerMatch && currentQuestion) {
      answerKeyId = answerMatch[1];
      continue;
    }

    // 4. Check if line is an Explanation
    const explanationMatch = cleanLine.match(explanationPattern);
    if (explanationMatch && currentQuestion) {
      currentExplanation = explanationMatch[1];
      continue;
    }

    // 5. Multi-line text handling
    if (currentQuestion) {
      if (currentOptions.length === 0 && !currentExplanation) {
        currentQuestion.question += '\n' + line;
      } else if (currentOptions.length > 0 && !answerKeyId && !currentExplanation) {
        currentOptions[currentOptions.length - 1].text += ' ' + line;
      } else if (currentExplanation) {
        currentExplanation += ' ' + line;
      }
    }
  }

  // Push the last question
  pushCurrentQuestion();

  return questions;
}
